/**
 * DecisionFlow — PlanTypeModifierGrid
 * ui/src/components/admin/PlanTypeModifierGrid.jsx
 * S611
 *
 * Pure rendering component — extracted from PlanDetailView.jsx.
 * Three-section expandable grid: Performance / Structural / Strategic.
 * 28 plan types. Strategic section default collapsed.
 * Role-category-aware card show/hide.
 * Coming Soon cards for post-GA plan types.
 * Combination rule advisories — dismissible per session (React state).
 *
 * Spec authority: comp_policies_spec_v2_2.md §8
 *
 * OWNERSHIP RULES (do not violate):
 *   - This component does NOT fetch data. PlanDetailView remains the data owner.
 *   - Renders cards based on props only.
 *   - onCardClick(modifierType) emits to PlanDetailView — no manager mounting here.
 *   - All amendment state, manager state, wizard session state stays in PlanDetailView.
 *   - This is a render-tree extraction only. No state moves. No data fetching moves.
 *
 * Props contract (strict — do not deviate):
 *   planId            {int|null}    — for manager component routing
 *   roleCategory      {string|null} — from role.role_category
 *   components        {array}       — from GET /api/v1/plan-components filtered by plan_id
 *   applicabilityRows {array}       — from GET /plan-components/{id}/modifier-applicability
 *                                     Shape: [{ modifier_type, applicability, modifier_status, modifier_entity_id }]
 *                                     Required for correct deriveCardState. PlanDetailView fetches and passes this.
 *   period            {object}      — { fiscal_period_id, period_status, ... }
 *   onCardClick       {function}    — (modifierType: string) => void
 *   isLocked          {bool}        — from period_status !== 'open'
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind colors. Zero dark: variants.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight, Lock, AlertTriangle, X } from "lucide-react";

// ---------------------------------------------------------------------------
// Card state style map — mirrors PlanDetailView STATE_STYLE
// OI-CARD-04 S733: approved/draft/pending cards get tinted backgrounds so
// configured vs unconfigured is immediately scannable at a glance.
// ---------------------------------------------------------------------------
const STATE_STYLE = {
  not_started:      { dot: "bg-[rgb(var(--color-border))]",   label: "Not configured",   text: "text-[rgb(var(--color-text-muted))]"  },
  draft:            { dot: "bg-[rgb(var(--color-warning))]",  label: "Draft",            text: "text-[rgb(var(--color-warning))]"     },
  pending_approval: { dot: "bg-[rgb(var(--color-info))]",     label: "Pending Approval", text: "text-[rgb(var(--color-info))]"        },
  approved:         { dot: "bg-[rgb(var(--color-success))]",  label: "Active",           text: "text-[rgb(var(--color-success))]"     },
  rejected:         { dot: "bg-[rgb(var(--color-danger))]",   label: "Rejected",         text: "text-[rgb(var(--color-danger))]"      },
  coming_soon:      { dot: "bg-[rgb(var(--color-border))]",   label: "Coming soon",      text: "text-[rgb(var(--color-text-muted))]"  },
  not_applicable:   { dot: "",                                 label: "",                 text: ""                                     },
};

// OI-CARD-04 S733 — PCMA modifier_type → grid card key reverse mapping.
// PCMA stores modifier_type as 'P3B-KICKER', 'P3B-REVTYPE', etc.
// CARD_DEFS uses keys like 'kicker_rules', 'revenue_type_rules'.
// Without this map, deriveCardState always returns 'not_started' even when
// PCMA rows exist — causing misleading "Not configured" state on configured plans.
const PCMA_TYPE_TO_CARD_KEY = {
  "P3B-CAP":      "accelerator_caps",
  "P3B-KICKER":   "kicker_rules",
  "P3B-REVTYPE":  "revenue_type_rules",
  "P3B-CLAWBACK": "churn_clawback",
  "P3B-DRAW":     "draw",
  "P3B-SPLIT":    "deal-split",
  "P3B-SPIFF":    "spiff",
  "P3B-TXN":      "transaction",
  "P4-TERRITORY": "territory_compensation",
  "P4-OVERLAY":   "overlay_attribution",
  "P4-COSELL":    "cosell_framework",
  "P4-TRUEUP":    "true_up_policy",
  "P4-POD":       "pod_compensation",
  "P4-CONTEST":   "contest_programs",
  "P4-BONUS":     "bonus_pool",
  "P4-NONCASH":   "non_cash_awards",
  "P4-MARKET":    "market_share",
  "P4-RANKING":   "ranking_plans",
  "P4-MARGIN":    "margin_compensation",
  "P4-MULTIYEAR": "multi_year_deals",
  "P4-RETENTION": "grr_nrr_retention",
  "P4-CHANNEL":   "channel_compensation",
  "P4-VOLUME":    "volume_compensation",
  "P4-PHARMA":    "pharma_script",
};

// ---------------------------------------------------------------------------
// Full 28-plan-type card definitions
// Sections: performance | structural | strategic
// role_categories: which role_category values show this card
//   null = always show in section
// prerequisite: advisory key — shown when prerequisite card is not approved
// ---------------------------------------------------------------------------
const CARD_DEFS = [
  // ── PERFORMANCE (always visible, 8 cards) ──
  {
    key: "core_ic_plan",      section: "performance", label: "Core IC Attainment",
    objective: "Drive quota attainment through rate-table payout escalation.",
    role_categories: null,
  },
  {
    key: "mbo_components",    section: "performance", label: "MBO Components",
    objective: "Reward non-revenue management objectives.",
    role_categories: null,
  },
  {
    key: "nps_components",    section: "performance", label: "NPS Components",
    objective: "Reward customer satisfaction outcomes.",
    role_categories: null,
  },
  {
    key: "adoption_components", section: "performance", label: "Adoption Components",
    objective: "Drive product adoption milestones post-sale.",
    role_categories: null,
  },
  {
    key: "accelerator_caps",  section: "performance", label: "Accelerator Cap",
    objective: "Protect from windfall at extreme attainment.",
    prerequisite: "core_ic_plan",
    role_categories: null,
  },
  {
    key: "kicker_rules",      section: "performance", label: "Kicker / Multiplier",
    objective: "Multiply payout above threshold — rewards breakthrough.",
    prerequisite: "core_ic_plan",
    role_categories: null,
  },
  {
    key: "revenue_type_rules", section: "performance", label: "Revenue-Type Rules",
    objective: "Pay different rates for new logo vs expansion vs renewal.",
    prerequisite: "core_ic_plan",
    role_categories: null,
  },
  {
    key: "churn_clawback",    section: "performance", label: "Clawback Policy",
    objective: "Recover payout on churned or cancelled revenue.",
    role_categories: null,
  },

  // ── STRUCTURAL (role-category-aware, up to 4 cards) ──
  {
    key: "draw",              section: "structural",  label: "Draw / Guarantee",
    objective: "Protect new hire cash flow during ramp period.",
    role_categories: ["ic", "hybrid", "transaction"],
  },
  {
    key: "deal-split",        section: "structural",  label: "Deal Splits",
    objective: "Govern credit attribution between co-sellers.",
    role_categories: ["ic", "hybrid"],
  },
  {
    key: "spiff",             section: "structural",  label: "SPIFF / Tactical",
    objective: "Create short-cycle intensity on specific product or segment.",
    role_categories: ["ic", "hybrid", "transaction"],
  },
  {
    key: "transaction",       section: "structural", label: "Transaction Commission",
    objective: "Reward individual transaction volume at fixed rates.",
    role_categories: ["ic", "hybrid", "transaction"],
  },

  // ── STRATEGIC (default collapsed, role-category-filtered defaults) ──
  {
    key: "territory_compensation", section: "strategic", label: "Territory Compensation",
    objective: "Assign and govern geographic or named account territory.",
    prerequisite: "core_ic_plan",
    role_categories: ["ic", "hybrid"],
    default_visible: ["ic", "hybrid"],
  },
  {
    key: "overlay_attribution", section: "strategic", label: "Overlay Attribution",
    objective: "Credit overlay specialists on deals sold by primary AE.",
    role_categories: ["ic", "hybrid"],
    default_visible: ["ic", "hybrid"],
  },
  {
    key: "cosell_framework",   section: "strategic",  label: "Co-Sell / Channel",
    objective: "Govern credit in partner and co-sell motions.",
    role_categories: ["ic", "hybrid"],
    default_visible: ["ic", "hybrid"],
  },
  {
    key: "true_up_policy",     section: "strategic",  label: "Retroactive True-Up",
    objective: "Correct prior-period payout when deal terms differ from booking.",
    role_categories: null,
  },
  {
    key: "pod_compensation",   section: "strategic",  label: "Pod / Team",
    objective: "Distribute team payout on collective performance.",
    prerequisite: "bonus_pool",
    role_categories: ["ic", "hybrid", "rollup"],
  },
  {
    key: "contest_programs",   section: "strategic",  label: "Contest Programs",
    objective: "Short-duration competitive program with defined winners.",
    role_categories: ["ic", "hybrid", "rollup", "transaction"],
    default_visible: ["ic", "hybrid", "rollup", "transaction"],
  },
  {
    key: "bonus_pool",         section: "strategic",  label: "Bonus Pool",
    objective: "Pre-fund pool and distribute on individual contribution.",
    role_categories: ["rollup", "hybrid"],
    default_visible: ["rollup", "hybrid"],
  },
  {
    key: "non_cash_awards",    section: "strategic",  label: "Non-Cash Awards",
    objective: "Recognize performance with non-monetary rewards.",
    role_categories: null,
    default_visible: ["ic", "hybrid", "transaction"],
  },
  {
    key: "market_share",       section: "strategic",  label: "Market Share",
    objective: "Pay on market penetration versus total addressable market.",
    prerequisite: "territory_compensation",
    role_categories: ["ic", "hybrid"],
  },
  {
    key: "ranking_plans",      section: "strategic",  label: "Ranking / Relative",
    objective: "Pay based on relative rank within peer group.",
    role_categories: null,
    default_visible: ["rollup", "hybrid"],
  },
  {
    key: "pharma_script",      section: "strategic",  label: "Pharma Script",
    objective: "Compensate on prescription volume.",
    prerequisite: "territory_compensation",
    role_categories: ["ic"],
  },

  {
    key: "plan_versioning",    section: "strategic",  label: "Plan Versioning",
    objective: "Govern mid-period plan changes with audit trail.",
    role_categories: null,
  },
  {
    key: "margin_compensation", section: "strategic", label: "Margin / Profit",
    objective: "Pay on margin or profit contribution.",
    role_categories: null,
  },
  {
    key: "multi_year_deals",   section: "strategic",  label: "Multi-Year Deal",
    objective: "Govern payout timing on multi-year contracts.",
    role_categories: null,
  },
  {
    key: "grr_nrr_retention",  section: "strategic",  label: "GRR / NRR Retention",
    objective: "Pay on gross or net revenue retention rates.",
    role_categories: null,
  },
  {
    key: "channel_compensation", section: "strategic", label: "Channel / Partner",
    objective: "Full partner comp: tiers, rebates, co-sell.",
    role_categories: null,
  },
  {
    key: "volume_compensation", section: "strategic", label: "Volume / Quantity",
    objective: "Pay on unit volume or quantity with tiered rates.",
    role_categories: null,
  },
];

// ---------------------------------------------------------------------------
// Role-category visibility rules
// ---------------------------------------------------------------------------
function isCardVisible(card, roleCategory) {
  if (!card.role_categories) return true;                       // null = always show
  if (!roleCategory) return true;                               // unknown role = show all
  return card.role_categories.includes(roleCategory);
}

// Derive card state from applicabilityRows (GET /plan-components/{id}/modifier-applicability).
// applicabilityRows have: { modifier_type, applicability, modifier_status, modifier_entity_id }
// modifier_type values are PCMA codes: 'P3B-KICKER', 'P3B-REVTYPE', etc.
// card.key values are grid keys: 'kicker_rules', 'revenue_type_rules', etc.
// OI-CARD-04 S733: use PCMA_TYPE_TO_CARD_KEY reverse map to correctly match rows.
// Falls back to "not_started" when applicabilityRows is empty or no matching row.
function deriveCardState(cardKey, applicabilityRows) {
  // Find the applicability row whose PCMA modifier_type maps to this card key
  const row = applicabilityRows.find(
    (r) => PCMA_TYPE_TO_CARD_KEY[r.modifier_type] === cardKey
  );
  if (!row) return "not_started";
  if (row.applicability === "not_applicable") return "not_applicable";
  // Map modifier_status to card display state
  const status = row.modifier_status ?? "not_started";
  if (status === "approved")         return "approved";
  if (status === "pending_approval") return "pending_approval";
  if (status === "rejected")         return "rejected";
  if (status === "draft")            return "draft";
  return "not_started";
}

// ---------------------------------------------------------------------------
// ModifierCard — pure render card (does not mount managers)
// ---------------------------------------------------------------------------
function ModifierCard({ card, state, isLocked, onClick }) {
  if (state === "not_applicable") return null;

  const style = STATE_STYLE[state] ?? STATE_STYLE.not_started;
  const isComingSoon = state === "coming_soon";

  if (isComingSoon) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-[rgb(var(--color-border))] p-4 opacity-60">
        <div className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">{card.label}</p>
        </div>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{card.objective}</p>
        <p className="text-tok-xs italic text-[rgb(var(--color-text-muted))]">Available in a future release.</p>
      </div>
    );
  }

  // OI-CARD-04 S733 — Background tint per state so configured vs unconfigured
  // is immediately scannable without reading the status label.
  const bgTint = state === "approved"
    ? "bg-[rgb(var(--color-success))]/5"
    : state === "pending_approval"
    ? "bg-[rgb(var(--color-info))]/5"
    : state === "draft"
    ? "bg-[rgb(var(--color-warning))]/5"
    : state === "rejected"
    ? "bg-[rgb(var(--color-danger))]/5"
    : "bg-[rgb(var(--color-surface))]";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !isLocked && onClick(card.key)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !isLocked && onClick(card.key)}
      className={[
        "relative flex flex-col gap-2 rounded-xl border p-4 transition-all",
        "focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]",
        bgTint,
        isLocked
          ? "cursor-default opacity-75"
          : "cursor-pointer hover:brightness-95",
        state === "approved"
          ? "border-[rgb(var(--color-success))]/60"
          : state === "pending_approval"
          ? "border-[rgb(var(--color-primary))]/60"
          : state === "draft"
          ? "border-[rgb(var(--color-warning))]/60"
          : state === "rejected"
          ? "border-[rgb(var(--color-danger))]/60"
          : "border-[rgb(var(--color-border))] hover:border-[rgb(var(--color-primary))]/40",
      ].join(" ")}
    >
      {/* Approved checkmark badge — top-right corner */}
      {state === "approved" && (
        <span className="absolute top-2.5 right-2.5 flex h-4 w-4 items-center justify-center
                         rounded-full bg-[rgb(var(--color-success))] text-white">
          <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
      <p className="text-tok-sm font-medium text-[rgb(var(--color-text))] pr-6">{card.label}</p>
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full shrink-0 ${style.dot}`} />
        <span className={`text-tok-xs font-medium ${style.text}`}>{style.label}</span>
      </div>
      {state === "not_started" && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Available to configure.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Advisory banner — dismissible per session
// ---------------------------------------------------------------------------
function AdvisoryBanner({ prerequisiteLabel, cardLabel, onDismiss }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-[rgb(var(--color-warning))]/30
                    bg-[rgb(var(--color-warning))]/5 px-3 py-2.5 mb-2">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
        <p className="text-tok-xs text-[rgb(var(--color-warning))]">
          <strong>{cardLabel}</strong> requires {prerequisiteLabel} to be configured first.
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------
function GridSection({ title, defaultOpen = true, children, count }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 py-1 text-left"
      >
        {open
          ? <ChevronDown className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
          : <ChevronRight className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />}
        <span className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          {title}
        </span>
        <div className="flex-1 h-px bg-[rgb(var(--color-border))]" />
        {!open && count != null && (
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{count} cards</span>
        )}
      </button>

      {open && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function PlanTypeModifierGrid({
  planId,
  roleCategory,
  components = [],
  applicabilityRows = [],
  period,
  onCardClick,
  isLocked = false,
  planApproved = false,  // OI-CARD-04 S733: suppress misleading prerequisite advisories on approved plans
}) {
  // Dismissed advisories — per session, React state only
  const [dismissedAdvisories, setDismissedAdvisories] = useState(new Set());

  function dismissAdvisory(key) {
    setDismissedAdvisories((prev) => new Set([...prev, key]));
  }

  // Build state map from applicabilityRows (OI-CARD-04 fix: uses PCMA_TYPE_TO_CARD_KEY).
  // When applicabilityRows is empty (no component selected yet), all cards show not_started.
  const stateMap = {};
  CARD_DEFS.forEach((card) => {
    stateMap[card.key] = deriveCardState(card.key, applicabilityRows);
  });

  // Check if prerequisite is satisfied
  function prerequisiteSatisfied(card) {
    if (!card.prerequisite) return true;
    return stateMap[card.prerequisite] === "approved";
  }

  // OI-CARD-04 S733: suppress advisory banners on approved plans — modifiers may be
  // fully configured even without PCMA rows (pre-S685 plans). Showing "requires Core IC
  // to be configured first" on an approved plan is misleading and creates false urgency.
  const pendingAdvisories = planApproved ? [] : CARD_DEFS.filter((card) =>
    card.prerequisite &&
    isCardVisible(card, roleCategory) &&
    !prerequisiteSatisfied(card) &&
    !dismissedAdvisories.has(card.key)
  );

  // Partition cards by section
  const performanceCards = CARD_DEFS.filter(
    (c) => c.section === "performance" && isCardVisible(c, roleCategory)
  );
  const structuralCards = CARD_DEFS.filter(
    (c) => c.section === "structural" && isCardVisible(c, roleCategory)
  );
  const strategicCards = CARD_DEFS.filter(
    (c) => c.section === "strategic" && isCardVisible(c, roleCategory)
  );

  // No components — show empty state with grid still visible
  const hasComponents = components.length > 0;

  if (!hasComponents && !planId) {
    return (
      <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-6 text-center">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No plan components yet</p>
        <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Add components to this plan to configure plan types.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Combination rule advisories */}
      {pendingAdvisories.length > 0 && (
        <div className="space-y-1.5">
          {pendingAdvisories.map((card) => {
            const prereq = CARD_DEFS.find((c) => c.key === card.prerequisite);
            return (
              <AdvisoryBanner
                key={card.key}
                prerequisiteLabel={prereq?.label ?? card.prerequisite}
                cardLabel={card.label}
                onDismiss={() => dismissAdvisory(card.key)}
              />
            );
          })}
        </div>
      )}

      {/* ── PERFORMANCE ── */}
      {performanceCards.length > 0 && (
        <GridSection title="Performance" defaultOpen={true} count={performanceCards.length}>
          {performanceCards.map((card) => (
            <ModifierCard
              key={card.key}
              card={card}
              state={hasComponents ? stateMap[card.key] : "not_started"}
              isLocked={isLocked}
              onClick={onCardClick}
            />
          ))}
        </GridSection>
      )}

      {/* ── STRUCTURAL ── */}
      {structuralCards.length > 0 && (
        <GridSection title="Structural" defaultOpen={true} count={structuralCards.length}>
          {structuralCards.map((card) => (
            <ModifierCard
              key={card.key}
              card={card}
              state={hasComponents ? stateMap[card.key] : "not_started"}
              isLocked={isLocked}
              onClick={onCardClick}
            />
          ))}
        </GridSection>
      )}

      {/* ── STRATEGIC ── */}
      {strategicCards.length > 0 && (
        <GridSection title="Strategic" defaultOpen={false} count={strategicCards.length}>
          {strategicCards.map((card) => (
            <ModifierCard
              key={card.key}
              card={card}
              state={hasComponents ? stateMap[card.key] : "not_started"}
              isLocked={isLocked}
              onClick={onCardClick}
            />
          ))}
        </GridSection>
      )}

    </div>
  );
}
